// The one time control for the whole UC CDR page: an explicit from → to, to the
// minute, in a named timezone. All the timezone arithmetic happens server-side
// (GET /uc-cdr/window) so the browser's own zone can never leak into a bill.
import { useEffect, useState } from "react";
import { CalendarClock, Globe2, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const PRESET_LABELS = [
  ["today", "Today"], ["yesterday", "Yesterday"], ["last_24h", "Last 24h"],
  ["last_7_days", "Last 7 days"], ["this_month", "This month"], ["last_month", "Last month"],
];
const TZ_LABELS = {
  "UTC": "UTC", "America/Edmonton": "Edmonton (Mountain)",
  "America/Vancouver": "Vancouver (Pacific)", "America/Toronto": "Toronto (Eastern)",
  "America/Halifax": "Halifax (Atlantic)",
};

export default function RangeBar({ value, onChange }) {
  const [draft, setDraft] = useState({ start: "", end: "", tz: value?.tz || "America/Edmonton" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!value) return;
    setDraft({ start: value.from_local || "", end: value.to_local || "", tz: value.tz });
  }, [value?.from_local, value?.to_local, value?.tz]);

  const apply = async (params) => {
    setBusy(true);
    try {
      const { data } = await api.get("/uc-cdr/window", { params });
      onChange(data);
    } finally {
      setBusy(false);
    }
  };

  const dirty = value && (draft.start !== (value.from_local || "") || draft.end !== (value.to_local || ""));
  const zones = value?.timezones || Object.keys(TZ_LABELS);

  return (
    <Card className="px-4 py-3" data-testid="uc-cdr-range">
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <Label className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
            From ({value?.abbrev || "—"})
          </Label>
          <Input type="datetime-local" className="h-9 w-[212px] text-[13px]" value={draft.start}
                 data-testid="uc-cdr-range-start"
                 onChange={(e) => setDraft((d) => ({ ...d, start: e.target.value }))} />
        </div>
        <div>
          <Label className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
            To ({value?.abbrev || "—"})
          </Label>
          <Input type="datetime-local" className="h-9 w-[212px] text-[13px]" value={draft.end}
                 data-testid="uc-cdr-range-end"
                 onChange={(e) => setDraft((d) => ({ ...d, end: e.target.value }))} />
        </div>
        <div>
          <Label className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
            Timezone
          </Label>
          <div className="flex items-center gap-1.5">
            <Globe2 className="h-4 w-4 text-slate-400" />
            <select value={draft.tz} data-testid="uc-cdr-range-tz"
                    onChange={(e) => apply({ start: draft.start, end: draft.end, tz: e.target.value })}
                    className="h-9 rounded-md border border-slate-200 bg-white px-2 text-[13px] text-slate-700">
              {zones.map((z) => <option key={z} value={z}>{TZ_LABELS[z] || z}</option>)}
            </select>
          </div>
        </div>
        <Button size="sm" variant={dirty ? "default" : "outline"} disabled={busy || !dirty}
                data-testid="uc-cdr-range-apply"
                onClick={() => apply({ start: draft.start, end: draft.end, tz: draft.tz })}>
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CalendarClock className="h-4 w-4" />} Apply
        </Button>
        <div className="ml-auto flex flex-wrap gap-1.5">
          {PRESET_LABELS.map(([key, label]) => (
            <Button key={key} size="sm" variant={value?.preset === key ? "default" : "ghost"}
                    className="h-8 px-2 text-[12px]" data-testid={`uc-cdr-preset-${key}`}
                    onClick={() => apply({ preset: key, tz: draft.tz })}>
              {label}
            </Button>
          ))}
        </div>
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-2 text-[11px] text-slate-500"
           data-testid="uc-cdr-range-label">
        <span className="font-semibold text-slate-600">{value?.label || "no window"}</span>
        <span className="text-slate-300">|</span>
        <span className="font-mono">{value?.from_at} → {value?.to_at}</span>
        <span>(UTC, half-open: the last instant belongs to the next window)</span>
      </div>
    </Card>
  );
}
